import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { EntitlementService } from '../../../core/services/entitlement.service';
import { UpgradeModalService, type UpgradeModalState } from '../../../core/services/upgrade-modal.service';
import { AuthService } from '../../../services/auth.service';
import { TranslationService } from '../../../services/translation.service';
import type { PublicPlan } from '../../../shared/models/plan.model';
import type { CapabilityKey } from '../../../shared/models/entitlements';
import type { PlansTranslations } from '../../../i18n/plans';
import en from '../../../i18n/en/plans';
import { PlansComponent } from '../../plans/plans.component';
import { PlanCheckoutComponent } from '../plan-checkout/plan-checkout.component';

/**
 * The in-place "pick a plan and pay" modal (#384). Opened by a locked control
 * or by an API answer saying the plan lacks a feature / has no quota left. Two
 * steps, no navigation: the same plan cards as /home/plans, then the payment
 * form for the chosen plan. When the payment goes through it waits for the
 * Stripe webhook to activate the plan, refreshes the user and entitlements,
 * and closes - so the user lands exactly where they were.
 */
@Component({
  selector: 'app-upgrade-modal',
  standalone: true,
  imports: [CommonModule, DialogModule, PlansComponent, PlanCheckoutComponent],
  templateUrl: './upgrade-modal.component.html',
  styleUrl: './upgrade-modal.component.css',
})
export class UpgradeModalComponent implements OnInit, OnDestroy {
  state: UpgradeModalState = { open: false, reason: 'generic', capability: null };
  chosenPlan: PublicPlan | null = null;
  activating = false;
  translations: PlansTranslations = en;

  private subscription = new Subscription();
  private activation: Subscription | null = null;

  constructor(
    private upgradeModal: UpgradeModalService,
    private entitlementService: EntitlementService,
    private authService: AuthService,
    private messageService: MessageService,
    private translationService: TranslationService
  ) {
    const current = this.translationService.getPlansTranslations();
    if (current) this.translations = current;
    this.translationService.onChange(() => {
      const next = this.translationService.getPlansTranslations();
      if (next) this.translations = next;
    });
  }

  get t(): PlansTranslations {
    return this.translations;
  }

  get subtitle(): string {
    if (this.state.reason === 'quota') return this.t.UPGRADE_SUBTITLE_QUOTA;
    // The paywall moment: the user has seen a preview of their tailored result.
    if (this.state.reason === 'preview' || this.state.capability === 'tailoring.fullResult') {
      return this.t.UPGRADE_SUBTITLE_PREVIEW;
    }
    return this.t.UPGRADE_SUBTITLE_AI;
  }

  ngOnInit(): void {
    this.subscription.add(
      this.upgradeModal.state$.subscribe((state) => {
        const wasOpen = this.state.open;
        this.state = state;
        if (!state.open && wasOpen) this.reset();
      })
    );
  }

  choose(plan: PublicPlan): void {
    this.chosenPlan = plan;
  }

  backToPlans(): void {
    this.chosenPlan = null;
  }

  close(): void {
    this.upgradeModal.close();
  }

  /** The user keeps the free plan: nothing to buy, just close. */
  keepFree(): void {
    this.close();
  }

  /** Pioneer claimed from inside the modal: the grant is already live. */
  onPioneerClaimed(): void {
    this.finish(true);
  }

  /** Payment confirmed. The plan turns active when Stripe's webhook reaches
   * the backend, a moment later - poll entitlements until it does. */
  onPaid(): void {
    this.activating = true;
    const capability = this.state.capability as CapabilityKey | null;
    let attempts = 0;
    this.activation?.unsubscribe();
    this.activation = timer(0, 1500).subscribe(() => {
      attempts++;
      this.authService.refreshUser().subscribe({ error: () => undefined });
      this.entitlementService.load().subscribe({
        next: () => {
          const ent = this.entitlementService.getEntitlements();
          const active = capability ? ent?.capabilities?.[capability] === true : (ent?.plan ?? 'free') !== 'free';
          if (active) this.finish(true);
          else if (attempts >= 10) this.finish(false);
        },
        error: () => {
          if (attempts >= 10) this.finish(false);
        },
      });
    });
  }

  /** `active` = the plan is confirmed; otherwise the payment went through but the
   * Stripe webhook has not landed yet, and we must not claim the plan is active. */
  private finish(active: boolean): void {
    this.activation?.unsubscribe();
    this.activation = null;
    this.activating = false;
    this.authService.refreshUser().subscribe({ error: () => undefined });
    this.entitlementService.load().subscribe({ error: () => undefined });
    this.messageService.add(
      active
        ? { severity: 'success', summary: this.t.UPGRADE_ACTIVE, life: 4000 }
        : { severity: 'info', summary: this.t.UPGRADE_PENDING, life: 8000 }
    );
    this.close();
  }

  private reset(): void {
    this.chosenPlan = null;
    this.activating = false;
    this.activation?.unsubscribe();
    this.activation = null;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.activation?.unsubscribe();
  }
}
